import type { WorkbookCatalogEntry } from '@erp/shared-types';

/** A Data Source as a list names it: its link id and raw `schema.table` name. */
export interface WorkbookSourceRef {
  id: string;
  name: string;
}

type LabeledEntry = Pick<WorkbookCatalogEntry, 'id' | 'label'>;

/**
 * What a person calls a Data Source rather than its `import.…` table: the
 * catalog's label (the Data Source's title and schema, "Gains Midwest / Item
 * Master"), less `schemaName` when every member of a group shares it. Keeps the
 * raw name when the catalog has no label for it, or the label says only the
 * schema (no title), which would name every member alike.
 */
export function workbookSourceLabel(
  source: WorkbookSourceRef,
  label: string | undefined,
  schemaName?: string,
): string {
  const trimmed = label?.trim();
  if (!trimmed) return source.name;
  if (!schemaName) return trimmed;
  if (trimmed === schemaName) return source.name;
  const suffix = ` / ${schemaName}`;
  return trimmed.endsWith(suffix)
    ? trimmed.slice(0, -suffix.length).trim() || source.name
    : trimmed;
}

/**
 * Labels many Data Sources against one `catalog` (see
 * {@link workbookSourceLabel}), looking each up by id once.
 */
export function workbookSourceLabeler(
  catalog: readonly LabeledEntry[],
  schemaName?: string,
): (source: WorkbookSourceRef) => string {
  const labels = new Map<string, string | undefined>(
    catalog.map((entry) => [entry.id, entry.label]),
  );
  return (source) =>
    workbookSourceLabel(source, labels.get(source.id), schemaName);
}
